// The customer page's Activity tab (REQ-243, SCR-592): the library's ActivityScreen over the
// org's audit (API-588) and its guardrail interventions; a prompt reader's guardrail rows link to
// the stored prompt. Its badge (R-10, formerly the Guardrails tab's) counts the guardrail rows
// the Log opens with. The sub-tab is the route's; `customerPath` builds its addresses. It reads
// the org's prompt logging on every sub-tab, since that decides whether Prompt logs is listed.
import { useEffect, useMemo, useState } from "preact/hooks";
import { ActivityScreen, guardrailCount, guardrailSource, LinkTabs, orgAuditSource, PromptLogsSubTab, promptLogSearch, useApi, type OrgContext, type ScopeMe, type TabLink } from "@nexus/ui";
import type { Session } from "../../session/session";
import { customerPath, withActivityBadge } from "./model";
import { holdsPromptReader, usePromptLogging } from "./prompt-logging";

/** A stored prompt's address under Activity → Prompt logs (SCR-593). */
const promptLogHref = (orgId: string, event: Parameters<typeof promptLogSearch>[0]): string => `${customerPath(orgId, "activity", "prompts")}?${promptLogSearch(event)}`;

export interface ActivityTabProps {
  orgId: string;
  session: Session;
  me: ScopeMe;
  subtab: string | undefined;
  /** The org context Prompt logs reads; null until the page has it. */
  context: OrgContext | null;
  query: URLSearchParams;
}

export function ActivityTab({ orgId, session, me, subtab, context, query }: ActivityTabProps) {
  const client = useApi();
  const reader = holdsPromptReader(session, orgId);
  const promptLogging = usePromptLogging(client, session, orgId);
  const sources = useMemo(
    () => [orgAuditSource(client, orgId), guardrailSource(client, orgId, reader ? { promptHref: (event) => promptLogHref(orgId, event) } : {})],
    [client, orgId, reader],
  );
  const scope = { me, promptLogging: promptLogging.state };
  const prompts = context && <PromptLogsSubTab context={context} query={query} scope={scope} onRetry={promptLogging.retry} />;
  return <ActivityScreen scope={scope} sources={sources} subtab={subtab} subtabHref={(id) => customerPath(orgId, "activity", id)} prompts={prompts} />;
}

/**
 * The tabs with the Activity tab's count of guardrail interventions (R-10). Rendered only for a
 * session that can open that tab, so nobody else's page asks for events it may not read; a failed
 * read leaves the tabs unbadged.
 */
export function ActivityBadgedTabs({ orgId, tabs, current, label }: { orgId: string; tabs: readonly TabLink[]; current: string; label: string }) {
  const client = useApi();
  const [count, setCount] = useState(0);
  useEffect(() => {
    let live = true;
    setCount(0);
    void guardrailCount(client, orgId, new Date()).then((n) => live && setCount(n));
    return () => {
      live = false;
    };
  }, [client, orgId]);
  return <LinkTabs tabs={withActivityBadge(tabs, count)} current={current} label={label} />;
}
